/**
 * Art Lembrar — lógica partilhada do carrinho (página /cart e drawer)
 * Ficheiro: assets/artlembrar-cart.js
 *
 * Tudo funciona por delegação de eventos, por isso continua a funcionar
 * quando o tema re-desenha o carrinho (morph) depois de mexer em quantidades,
 * remover artigos ou adicionar produtos.
 */
import { CartAddEvent } from '@theme/events';

if (!window.__alCartInit) {
  window.__alCartInit = true;

  /* ==========================================================
     1. CONSENTIMENTO PARA REDES SOCIAIS
     ========================================================== */
  const CONSENT_ATTR = 'Permissão para gravar nas redes sociais';

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.cart-social-consent .cart-consent-btn');
    if (!btn) return;
    e.preventDefault();

    const box = btn.closest('.cart-social-consent');
    const value = btn.dataset.consent;

    box.querySelectorAll('.cart-consent-btn').forEach((b) => b.classList.toggle('active', b === btn));
    const input = box.querySelector('input[type="hidden"]');
    if (input) {
      input.value = value;
      input.setAttribute('value', value);
    }
    box.classList.remove('shake');
    box.querySelector('.consent-error')?.classList.remove('show');

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { [CONSENT_ATTR]: value } }),
    }).catch(() => {});
  });

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

      const box = document.querySelector('.cart-social-consent');
      if (!box) return;
      const input = box.querySelector('input[type="hidden"]');
      if (input && input.value) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      box.querySelector('.consent-error')?.classList.add('show');
      box.classList.remove('shake');
      void box.offsetWidth;
      box.classList.add('shake');
      setTimeout(() => box.classList.remove('shake'), 500);
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    true
  );

  /* ==========================================================
     2. RESERVA DO CARRINHO (30 MINUTOS)
     O tempo começa na primeira vez que o aviso fica visível
     (página do carrinho ou drawer aberto) e é partilhado entre os dois.
     ========================================================== */
  const RES_KEY = 'al_cart_reserved_until';
  const RES_DURATION = 30 * 60 * 1000;
  let resExpiredThisPage = false;

  const store = {
    get() {
      try { return parseInt(localStorage.getItem(RES_KEY) || '0', 10) || 0; } catch (_) { return 0; }
    },
    set(v) {
      try { localStorage.setItem(RES_KEY, String(v)); } catch (_) {}
    },
    clear() {
      try { localStorage.removeItem(RES_KEY); } catch (_) {}
    },
  };

  const fmt = (ms) => {
    const t = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(t / 60);
    const s = t % 60;
    return (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
  };

  function reserveTick() {
    const root = document.documentElement;
    const els = document.querySelectorAll('[data-al-reserve]');

    if (!els.length || resExpiredThisPage) {
      root.classList.remove('al-reserve-on');
      return;
    }

    const anyVisible = Array.from(els).some((el) => el.parentElement && el.parentElement.getClientRects().length > 0);
    const now = Date.now();
    let until = store.get();

    if (until && now >= until) {
      store.clear();
      resExpiredThisPage = true;
      root.classList.remove('al-reserve-on');
      return;
    }

    if (!until) {
      if (!anyVisible) return;
      until = now + RES_DURATION;
      store.set(until);
    }

    const label = fmt(until - now);
    els.forEach((el) => {
      const t = el.querySelector('[data-al-reserve-time]');
      if (!t || t.textContent === label) return;
      if (t.firstChild && t.firstChild.nodeType === 3 && t.childNodes.length === 1) t.firstChild.data = label;
      else t.textContent = label;
    });
    root.classList.add('al-reserve-on');
  }

  setInterval(reserveTick, 1000);
  reserveTick();

  // Depois de cada re-desenho do carrinho, acerta logo o relógio (evita mostrar 30:00 por um segundo)
  let reserveRaf = 0;
  new MutationObserver(() => {
    if (reserveRaf) return;
    reserveRaf = requestAnimationFrame(() => {
      reserveRaf = 0;
      reserveTick();
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });

  /* ==========================================================
     3. ARTIGOS SEM PERSONALIZAÇÃO (fallback no drawer)
     Se o carrinho foi re-desenhado e o botão perdeu o script,
     manda o cliente para a página do carrinho, que resolve.
     ========================================================== */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.cart-drawer #al-pers-corrigir');
    if (!btn) return;
    setTimeout(() => {
      if (!btn.disabled) window.location.href = '/cart';
    }, 0);
  });

  /* ==========================================================
     4. SETAS DO UPSELL NO DRAWER
     ========================================================== */
  document.addEventListener('click', (e) => {
    const arrow = e.target.closest('[data-al-dup-dir]');
    if (!arrow) return;
    const track = arrow.closest('.al-dup')?.querySelector('[data-al-dup-track]');
    if (!track) return;
    const dir = parseInt(arrow.dataset.alDupDir, 10) || 1;
    track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
  });

  /* ==========================================================
     5. ADICIONAR AO CARRINHO POR APPS (ex.: Live Product Options)
     Se uma app adicionar ao carrinho por fetch sem avisar o tema,
     atualiza o drawer e abre-o, como faz o botão normal do Horizon.
     ========================================================== */
  let lastThemeCartEvent = 0;
  document.addEventListener('cart:update', () => {
    lastThemeCartEvent = Date.now();
  });

  /* ==========================================================
     6. "EDITAR" DA LPO: REPOR TODAS AS ESCOLHAS
     O editar da LPO só repõe as opções da config principal do produto.
     As configs extra (Pingentes, Embrulho) ficavam por escolher.
     Guardamos as propriedades do artigo ao carregar em Editar e,
     quando as opções aparecem, selecionamos o que ficou em falta.
     ========================================================== */
  const EDIT_KEY = 'al_edit_props';
  const EDIT_TTL = 2 * 60 * 1000;

  window.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest && e.target.closest('[data-live-item-edit]');
      if (!btn) return;
      const key = btn.getAttribute('data-live-item-edit');
      // Fazemos nós a navegação: a página abre sempre do zero, como no primeiro Editar.
      // Editar sem sair da página deixava a LPO limpar o pingente e o embrulho.
      e.preventDefault();
      e.stopImmediatePropagation();
      const fallbackHref = btn.getAttribute('href');
      const go = (item) => {
        let url = '';
        if (item && item.url) {
          url = item.url + (item.url.indexOf('?') === -1 ? '?' : '&') + 'item=' + encodeURIComponent(key);
        } else if (fallbackHref && fallbackHref !== '#') {
          url = fallbackHref;
        }
        if (url) window.location.href = url;
      };
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          const item = (cart.items || []).find((i) => i.key === key);
          if (!item || !item.properties) {
            go(item);
            return;
          }
          const props = {};
          Object.keys(item.properties).forEach((k) => {
            const v = item.properties[k];
            if (k.charAt(0) === '_' || v == null || v === '') return;
            props[k] = String(v);
          });
          try {
            sessionStorage.setItem(EDIT_KEY, JSON.stringify({ t: Date.now(), props }));
          } catch (_) {}
          go(item);
        })
        .catch(() => go(null));
    },
    true
  );

  function readEdit() {
    try {
      const d = JSON.parse(sessionStorage.getItem(EDIT_KEY) || 'null');
      if (!d || Date.now() - d.t > EDIT_TTL) return null;
      return d.props;
    } catch (_) {
      return null;
    }
  }

  function cssEscape(v) {
    return window.CSS && CSS.escape ? CSS.escape(v) : v.replace(/["\\]/g, '\\$&');
  }

  /* Durante uns segundos depois de carregar em Editar, as escolhas guardadas são
     mantidas: se a LPO as limpar por baixo (acontecia ao editar outra vez sem sair
     da página), voltam a ser marcadas. Assim que o cliente mexe numa opção,
     essa opção deixa de ser tocada, para nunca desfazer uma escolha dele. */
  let userTouched = new Set();
  let reapplied = {};
  const MAX_REAPPLY = 6;

  function propName(el) {
    const m = el && el.name && el.name.match(/^properties\[(.*)\]$/);
    return m ? m[1] : null;
  }

  ['change', 'input'].forEach((type) => {
    document.addEventListener(
      type,
      (e) => {
        if (!e.isTrusted) return; // mudanças feitas por código (nossas ou da LPO) não contam
        const n = propName(e.target);
        if (n) userTouched.add(n);
      },
      true
    );
  });

  /* Devolve { missing, changed }: quantas faltam encontrar e quantas tiveram de ser repostas */
  function applyEdit() {
    const props = readEdit();
    if (!props) return { missing: 0, changed: 0 };
    let missing = 0;
    let changed = 0;

    Object.keys(props).forEach((name) => {
      if (userTouched.has(name)) return;
      if ((reapplied[name] || 0) >= MAX_REAPPLY) return;
      const value = props[name];
      const fields = document.querySelectorAll('[name="properties[' + cssEscape(name) + ']"]');
      if (!fields.length) {
        missing++;
        return;
      }

      const choices = Array.from(fields).filter((f) => f.type === 'radio' || f.type === 'checkbox');
      if (choices.length) {
        const match = choices.find((f) => f.value === value);
        if (match && !match.checked) {
          match.click();
          reapplied[name] = (reapplied[name] || 0) + 1;
          changed++;
        }
        return;
      }

      const field = fields[0];
      if (field.tagName === 'SELECT') {
        if (field.value !== value && Array.from(field.options).some((o) => o.value === value)) {
          field.value = value;
          field.dispatchEvent(new Event('change', { bubbles: true }));
          reapplied[name] = (reapplied[name] || 0) + 1;
          changed++;
        }
        return;
      }

      if ((field.tagName === 'INPUT' && field.type !== 'hidden' && field.type !== 'file') || field.tagName === 'TEXTAREA') {
        if (field.value === '') {
          field.value = value;
          field.dispatchEvent(new Event('input', { bubbles: true }));
          field.dispatchEvent(new Event('change', { bubbles: true }));
          reapplied[name] = (reapplied[name] || 0) + 1;
          changed++;
        }
      }
    });

    return { missing, changed };
  }

  let editTimer = 0;
  function startEditFill() {
    clearInterval(editTimer);
    userTouched = new Set();
    reapplied = {};
    const started = Date.now();
    let stableSince = 0;
    editTimer = setInterval(() => {
      const props = readEdit();
      if (!props || Date.now() - started > 15000) {
        clearInterval(editTimer);
        editTimer = 0;
        try { sessionStorage.removeItem(EDIT_KEY); } catch (_) {}
        return;
      }
      const r = applyEdit();
      // Termina quando tudo está no sítio há 4 segundos seguidos
      if (r.missing === 0 && r.changed === 0) {
        if (!stableSince) stableSince = Date.now();
        if (Date.now() - stableSince >= 4000) {
          clearInterval(editTimer);
          editTimer = 0;
          try { sessionStorage.removeItem(EDIT_KEY); } catch (_) {}
        }
      } else {
        stableSince = 0;
      }
    }, 300);
  }

  // Se o Editar levar a outra página, continua lá
  if (readEdit()) startEditFill();
  window.addEventListener('options:rendered', () => {
    if (readEdit() && !editTimer) startEditFill();
  });

  /* ==========================================================
     7. DEPOIS DE GUARDAR O "EDITAR" DA LPO: ABRIR O DRAWER
     A LPO manda para a página /cart depois de editar, a não ser que
     o tema defina window.clOnCartUpdate (documentado pela Cloudlift).
     ========================================================== */
  window.clOnCartUpdate = function () {
    window.__alEditSaved = true;
    window.dispatchEvent(new Event('al:edit-saved'));
    // Na própria página do carrinho não há drawer: recarrega para mostrar a alteração
    if (!document.querySelector('cart-drawer-component')) {
      window.location.reload();
      return;
    }
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        document.dispatchEvent(
          new CartAddEvent({}, 'artlembrar-edit', {
            source: 'artlembrar-edit',
            itemCount: cart.item_count,
          })
        );
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      })
      .catch(() => {
        window.location.href = '/cart';
      });
  };

  /* ==========================================================
     8. DEPOIS DE ADICIONAR AO CARRINHO: LIMPAR TAMBÉM PINGENTE E EMBRULHO
     A LPO guarda uma lista dos valores iniciais (_optionValuesClear) e repõe-na
     depois de adicionar. Essa lista só tem as opções da config principal, por
     isso as configs extra (Pingentes, Embrulho) ficavam marcadas.
     Juntamos à lista todas as opções que faltam, com valor vazio, e a própria
     LPO passa a limpá-las (seleção e preço).
     ========================================================== */
  function patchLpoClearList() {
    const app = window.Cloudlift && window.Cloudlift.options && window.Cloudlift.options.App;
    if (!app || !Array.isArray(app.products)) return;
    app.products.forEach((product) => {
      if (!product || typeof product.getValues !== 'function') return;
      if (!product._optionValuesClear) return;
      let values;
      try { values = product.getValues(); } catch (_) { return; }
      Object.keys(values || {}).forEach((name) => {
        if (!(name in product._optionValuesClear)) product._optionValuesClear[name] = '';
      });
    });
  }

  window.addEventListener('options:rendered', patchLpoClearList);
  // A LPO pode ter carregado antes deste ficheiro: tenta também nos primeiros segundos
  [300, 1000, 2500, 5000].forEach((ms) => setTimeout(patchLpoClearList, ms));

  /* ==========================================================
     9. PÁGINA DE PRODUTO EM MODO "EDITAR" (?item=...)
        - Só conta como edição se esse artigo ainda existir no carrinho
          (depois de guardar, a chave do artigo muda e o link fica velho).
        - Botão: "Guardar alterações"; depois de guardar, "Alterações guardadas".
        - Depois de guardar, o ?item= sai do endereço: ao atualizar a página
          fica um produto normal, com "Adicionar ao carrinho", para quem
          quiser juntar outra peça igual.
     ========================================================== */
  (function () {
    let itemKey = null;
    try { itemKey = new URLSearchParams(location.search).get('item'); } catch (_) {}
    if (!itemKey) return;

    const small = () => window.matchMedia('(max-width: 749px)').matches;
    let LABEL = 'Guardar alterações';
    let DONE = 'Alterações guardadas';
    const setLabels = () => {
      LABEL = small() ? 'Guardar' : 'Guardar alterações';
      DONE = small() ? 'Guardado' : 'Alterações guardadas';
    };
    setLabels();
    window.addEventListener('resize', setLabels);

    // Botão ao lado da quantidade, mesmo com o texto de edição
    document.documentElement.classList.add('al-edit-mode');
    if (!document.getElementById('al-edit-btn-css')) {
      const st = document.createElement('style');
      st.id = 'al-edit-btn-css';
      st.textContent =
        '@media (max-width: 749px){' +
        'html.al-edit-mode .product-form-buttons{flex-wrap:nowrap!important}' +
        'html.al-edit-mode .product-form-buttons button[name="add"]{min-width:0!important;flex:1 1 auto!important}' +
        'html.al-edit-mode button[name="add"] .add-to-cart-text__content,' +
        'html.al-edit-mode button[name="add"] .add-to-cart-text--added span:last-child' +
        '{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
        '}';
      document.head.appendChild(st);
    }
    let active = true;
    let photosExpected = null; // null = ainda não se sabe
    const originals = new WeakMap();

    function dropItemParam() {
      try {
        const u = new URL(location.href);
        u.searchParams.delete('item');
        history.replaceState(history.state, '', u.toString());
      } catch (_) {}
    }

    function relabel() {
      document.querySelectorAll('button[name="add"]').forEach((btn) => {
        const main = btn.querySelector('.add-to-cart-text__content');
        const added = btn.querySelector('.add-to-cart-text--added span:last-child');
        if (main && !originals.has(main)) originals.set(main, main.textContent);
        if (added && !originals.has(added)) originals.set(added, added.textContent);

        if (!active) {
          document.documentElement.classList.remove('al-edit-mode');
          // Voltou a ser produto normal: repõe os textos do tema
          if (main && originals.has(main) && main.textContent !== originals.get(main)) main.textContent = originals.get(main);
          if (added && originals.has(added) && added.textContent !== originals.get(added)) added.textContent = originals.get(added);
          return;
        }
        const want = window.__alEditSaved ? DONE : LABEL;
        if (main && main.textContent.trim() !== want) main.textContent = want;
        if (added && added.textContent.trim() !== DONE) added.textContent = DONE;
      });
    }

    // Confirmar que o artigo ainda está no carrinho
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        const item = (cart.items || []).find((i) => i.key === itemKey);
        const exists = !!item;
        // Quantas fotos tinha o artigo guardado: só essas caixas ficam "a carregar"
        photosExpected = 0;
        if (item && item.properties) {
          Object.keys(item.properties).forEach((k) => {
            if (k.charAt(0) === '_') return;
            String(item.properties[k] || '').split(',').forEach((v) => {
              if (v.indexOf('upload.cloudlift.app') !== -1) photosExpected++;
            });
          });
        }
        if (!exists) {
          active = false;
          dropItemParam();
          relabel();
        }
      })
      .catch(() => {});

    window.addEventListener('al:edit-saved', () => {
      dropItemParam();
      relabel();
    });

    relabel();
    document.addEventListener('DOMContentLoaded', relabel);
    let raf = 0;
    new MutationObserver(() => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; relabel(); });
    }).observe(document.documentElement, { childList: true, subtree: true });

    /* Enquanto a foto do carrinho ainda não chegou, a caixa mostrava
       "Escolha o ficheiro". Nos primeiros segundos da edição, as caixas
       visíveis ainda vazias dizem "A carregar a foto...". */
    /* Só se mostra "A carregar a foto..." se o artigo guardado TINHA fotos,
       e só até essas fotos aparecerem. Se a cliente trocar de texto para foto
       ao editar, a caixa nova aparece logo com "Escolher foto". */
    const startedAt = Date.now();
    // Mais fotos, mais tempo: 5 segundos por foto, mínimo 15 e máximo 60
    const limit = () => Math.min(60000, Math.max(15000, (photosExpected || 0) * 5000));
    const isMulti = (root) =>
      root.classList.contains('al-up-multi') ||
      !!root.querySelector('.filepond--browser[multiple]') ||
      root.querySelectorAll('.filepond--item').length > 1;
    const loadingTimer = setInterval(() => {
      const late = Date.now() - startedAt > limit();
      const roots = Array.from(document.querySelectorAll('.cl-po--upload-lift .filepond--root'));
      const restored = roots.reduce((n, r) => n + r.querySelectorAll('.filepond--item').length, 0);
      const waiting = active && !late && photosExpected !== null && photosExpected > 0 && restored < photosExpected;
      roots.forEach((root) => {
        if (isMulti(root)) root.classList.add('al-up-multi');
        const empty = !root.querySelector('.filepond--item');
        root.classList.toggle('al-up-waiting', waiting && empty);
      });
      if (late || (photosExpected !== null && restored >= photosExpected)) {
        roots.forEach((root) => root.classList.remove('al-up-waiting'));
        clearInterval(loadingTimer);
      }
    }, 250);

    /* A LPO repõe o valor da foto mas o campo de upload só a mostra
       depois de a opção ser desenhada outra vez (via-se ao carregar em
       "Guardar alterações", que valida e redesenha). Pedimos esse
       redesenho nós, se a caixa continuar vazia. */
    function rerenderIfEmpty() {
      if (!active || !photosExpected) return;
      const roots = Array.from(document.querySelectorAll('.cl-po--upload-lift .filepond--root'))
        .filter((r) => r.offsetHeight && !r.classList.contains('cl-hide'));
      if (!roots.length) return;
      const stillEmpty = roots.some((r) => !r.querySelector('.filepond--item'));
      if (!stillEmpty) return;
      const app = window.Cloudlift && window.Cloudlift.options && window.Cloudlift.options.App;
      (app && Array.isArray(app.products) ? app.products : []).forEach((product) => {
        try { if (typeof product.render === 'function') product.render(); } catch (_) {}
      });
    }
    [1500, 3500, 7000].forEach((ms) => setTimeout(rerenderIfEmpty, ms));
  })();

  /* ==========================================================
     10. UPLOAD DE FOTOS (Upload-Lift) AO EDITAR: ASPETO
        Quando a foto já vem do carrinho, o item fica num estado que não é
        "a enviar" nem "concluído" (idle, loading...). O FilePond esconde
        aí o texto de estado e o CSS da LPO não tinha regra para isso.
        Mostramos "✓ Foto carregada" centrado e o X passa para a direita.
     ========================================================== */
  (function () {
    if (document.getElementById('al-upload-edit-css')) return;
    // Só nas caixas de UMA foto. As de várias fotos ficam com o aspeto normal do Upload-Lift.
    const ONE = '.cl-po--upload-lift .filepond--root:not(.al-up-multi):not(:has(.filepond--browser[multiple]))';
    const S = ONE + ' .filepond--item:not([data-filepond-item-state~="processing"]):not([data-filepond-item-state="processing-complete"]):not([data-filepond-item-state*="error"]):not([data-filepond-item-state*="invalid"])';
    const css = `
      ${ONE}:has(${S}) {
        height: 52px !important;
      }
      ${S} .filepond--file-status {
        opacity: 0 !important;
      }
      ${S}::after {
        content: "\\2713  Foto carregada";
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding-right: 28px;
        font-family: Raleway, sans-serif;
        font-size: 14px;
        color: #374151;
        white-space: pre;
        pointer-events: none;
        z-index: 1;
      }
      ${S} .filepond--action-remove-item {
        left: auto !important;
        right: 0.6em !important;
        z-index: 2;
      }

      /* Edição: caixa ainda vazia enquanto a foto do carrinho chega */
      .cl-po--upload-lift .filepond--root.al-up-waiting .filepond--drop-label {
        position: relative;
      }
      .cl-po--upload-lift .filepond--root.al-up-waiting .filepond--drop-label > *,
      .cl-po--upload-lift .filepond--root.al-up-waiting .filepond--drop-label > * * {
        visibility: hidden !important;
      }
      .cl-po--upload-lift .filepond--root.al-up-multi.al-up-waiting .filepond--drop-label::after {
        content: "A carregar as fotos..." !important;
      }
      .cl-po--upload-lift .filepond--root.al-up-waiting .filepond--drop-label::after {
        content: "A carregar a foto...";
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: Raleway, sans-serif;
        font-size: 14px;
        color: #6b7280;
        pointer-events: none;
      }
    `;
    const style = document.createElement('style');
    style.id = 'al-upload-edit-css';
    style.textContent = css;
    document.head.appendChild(style);

    // Marca as caixas com mais de uma foto (ficam com o aspeto normal)
    let raf = 0;
    const markMulti = () => {
      raf = 0;
      document.querySelectorAll('.cl-po--upload-lift .filepond--root:not(.al-up-multi)').forEach((root) => {
        if (root.querySelector('.filepond--browser[multiple]') || root.querySelectorAll('.filepond--item').length > 1) {
          root.classList.add('al-up-multi');
        }
      });
    };
    new MutationObserver(() => {
      if (!raf) raf = requestAnimationFrame(markMulti);
    }).observe(document.documentElement, { childList: true, subtree: true });
  })();

  const originalFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    const promise = originalFetch(input, init);
    let url = '';
    try {
      url = typeof input === 'string' ? input : (input && input.url) || String(input);
    } catch (_) {}

    if (/\/cart\/(add|change)(\.js)?(\?|$)/.test(url) && /[?&]item=/.test(location.search)) {
      promise.then((res) => {
        if (res.ok) {
          window.__alEditSaved = true;
          window.dispatchEvent(new Event('al:edit-saved'));
        }
      }).catch(() => {});
    }

    if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
      const started = Date.now();
      promise
        .then((res) => {
          if (!res.ok) return;
          // Garante que a LPO também limpa pingente/embrulho a seguir
          patchLpoClearList();
          setTimeout(() => {
            if (lastThemeCartEvent >= started) return; // o tema já tratou
            if (!document.querySelector('cart-drawer-component')) return; // estamos na página /cart
            originalFetch('/cart.js')
              .then((r) => r.json())
              .then((cart) => {
                document.dispatchEvent(
                  new CartAddEvent({}, 'artlembrar-app', {
                    source: 'artlembrar-app',
                    itemCount: cart.item_count,
                  })
                );
              })
              .catch(() => {});
          }, 600);
        })
        .catch(() => {});
    }

    return promise;
  };
}